import React, { FunctionComponent, useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts'

import {
  COLOR_SCHEMES,
  formatChartCurrency,
} from '../../utils/buybackPayoutHelpersNew'
import { BuybackFormPayoutChartLegend } from '../BuybackFormPayoutChartLegend/BuybackFormPayoutChartLegend'

export interface BuybackFormPayoutChartProps {
  offerPrice: number
  commission: number
  debtFeeTotal: number
  debtAmountTotal: number
  processCostTotal: number
  requested: number
}

interface StackedBarData {
  name: string
  payout: number
  commission: number
  debtFee: number
  debtAmount: number
  processCost: number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderCustomLabel = (props: any) => {
  const x = Number(props.x ?? 0)
  const y = Number(props.y ?? 0)
  const width = Number(props.width ?? 0)
  const height = Number(props.height ?? 0)
  const value = Number(props.value ?? 0)

  if (width < 50 || value === 0) return null

  return (
    <text
      x={x + width / 2}
      y={y + height / 2}
      fill="#1f2937"
      textAnchor="middle"
      dominantBaseline="middle"
      className="text-xs opacity-70"
    >
      {formatChartCurrency(value)}
    </text>
  )
}

export const BuybackFormPayoutChart: FunctionComponent<BuybackFormPayoutChartProps> =
  ({
    offerPrice,
    commission,
    debtFeeTotal,
    debtAmountTotal,
    processCostTotal,
    requested,
  }) => {
    const totalOutcome =
      commission + debtFeeTotal + debtAmountTotal + processCostTotal
    const payout = offerPrice - totalOutcome

    const status: 'red' | 'orange' | 'green' =
      payout < 0 ? 'red' : payout < requested ? 'orange' : 'green'

    const colors = COLOR_SCHEMES[status]

    const data = useMemo<StackedBarData[]>(
      () => [
        {
          name: 'Nabídka výkupu',
          payout: payout > 0 ? payout : 0,
          commission,
          debtFee: debtFeeTotal,
          debtAmount: debtAmountTotal,
          processCost: processCostTotal,
        },
      ],
      [payout, commission, debtFeeTotal, debtAmountTotal, processCostTotal]
    )

    return (
      <div className="mb-8">
        <ResponsiveContainer width="100%" height={50}>
          <BarChart
            data={data}
            layout="vertical"
            margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            barSize={50}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <YAxis
              type="category"
              dataKey="name"
              angle={-90}
              textAnchor="end"
              height={80}
              label={{ value: '', angle: 0, position: 'insideLeft' }}
              hide
            />
            <XAxis
              type="number"
              tickFormatter={formatChartCurrency}
              padding={{ left: 0, right: 0 }}
              label={{
                value: '0 Kč',
                position: 'insideLeft',
              }}
            />

            {debtAmountTotal > 0 && (
              <Bar
                dataKey="debtAmount"
                stackId="a"
                fill={colors.debtAmount}
                name="Závazky"
                radius={[8, 0, 0, 8]}
              >
                <LabelList dataKey="debtAmount" content={renderCustomLabel} />
              </Bar>
            )}

            {debtFeeTotal > 0 && (
              <Bar
                dataKey="debtFee"
                stackId="a"
                fill={colors.debtFee}
                name="Vyřízení závazků"
                radius={[0, 0, 0, 0]}
              >
                <LabelList dataKey="debtFee" content={renderCustomLabel} />
              </Bar>
            )}

            {processCostTotal > 0 && (
              <Bar
                dataKey="processCost"
                stackId="a"
                fill={colors.processCost}
                name="Adminis. poplatky"
                radius={[0, 0, 0, 0]}
              >
                <LabelList dataKey="processCost" content={renderCustomLabel} />
              </Bar>
            )}

            {commission > 0 && (
              <Bar
                dataKey="commission"
                stackId="a"
                fill={colors.commission}
                name="Provize"
                radius={[0, 0, 0, 0]}
              >
                <LabelList dataKey="commission" content={renderCustomLabel} />
              </Bar>
            )}

            {payout > 0 && (
              <Bar
                dataKey="payout"
                stackId="a"
                fill={colors.payout}
                name="K vyplacení"
                radius={[0, 8, 8, 0]}
              >
                <LabelList dataKey="payout" content={renderCustomLabel} />
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>

        <BuybackFormPayoutChartLegend
          debtAmountTotal={debtAmountTotal}
          debtFeeTotal={debtFeeTotal}
          processCostTotal={processCostTotal}
          commission={commission}
          payout={payout}
          offerPrice={offerPrice}
          status={status}
        />
      </div>
    )
  }
